/**
 * Converts HTML to Quill-compatible format
 * Handles font tags, headers, lists, and preserves styling
 */
export const convertToQuillCompatible = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    // Step 0: Remove empty <p> tags (containing only whitespace, <br>, or nothing), keeping only one
    const allParagraphs = doc.querySelectorAll('p');
    let emptyPCount = 0;
    const emptyPs = [];
    allParagraphs.forEach((p) => {
        const textContent = p.textContent?.trim() || '';
        const innerHTML = p.innerHTML.trim();
        // Check if paragraph is empty (only whitespace, <br>, or nothing)
        const isEmpty = textContent === '' || innerHTML === '' || innerHTML === '<br>' || innerHTML === '<br/>' || innerHTML.match(/^<br\s*\/?>$/i);
        if (isEmpty) {
            emptyPs.push(p);
            emptyPCount++;
        }
    });
    // Remove all empty <p> tags except keep one (the first one found)
    if (emptyPCount > 1) {
        // Keep the first empty <p>, remove the rest
        for (let i = 1; i < emptyPs.length; i++) {
            const emptyP = emptyPs[i];
            emptyP.parentNode?.removeChild(emptyP);
        }
    }
    // Step 1: Convert <font> tags to <span> with proper styling
    const fontElements = doc.querySelectorAll('font');
    fontElements.forEach((font) => {
        const span = doc.createElement('span');
        const styles = [];
        if (font.getAttribute('color')) {
            const colorValue = font.getAttribute('color');
            let formattedColor = colorValue || '';
            if (formattedColor && !formattedColor.startsWith('#') && /^[0-9a-fA-F]{3,6}$/.test(formattedColor)) {
                formattedColor = `#${formattedColor}`;
            }
            styles.push(`color: ${formattedColor}`);
        }
        if (font.getAttribute('face')) {
            const faceValue = font.getAttribute('face') || '';
            // Normalize all incoming font faces to a single font-family for consistent rendering
            styles.push('font-family: "Poppins"');
        }
        else {
            const parent = font.parentElement;
            const isInList = parent?.closest('li, ul, ol');
            if (isInList) {
                styles.push('font-family: "Poppins"');
            }
        }
        if (font.getAttribute('size')) {
            const size = font.getAttribute('size');
            const sizeMap = {
                '1': '10pt',
                '2': '12pt',
                '3': '14pt',
                '4': '16pt',
                '5': '18pt',
                '6': '24pt',
                '7': '36pt'
            };
            if (size && sizeMap[size]) {
                styles.push(`font-size: ${sizeMap[size]}`);
            }
        }
        if (font.getAttribute('style')) {
            styles.push(font.getAttribute('style'));
        }
        if (styles.length > 0) {
            span.setAttribute('style', styles.join('; '));
        }
        while (font.firstChild) {
            span.appendChild(font.firstChild);
        }
        font.parentNode?.replaceChild(span, font);
    });
    // Step 1.5: Normalize any inline font-family to a single family (handles existing <span style="font-family: ...">)
    const elementsWithFontFamily = doc.querySelectorAll('[style*="font-family"]');
    elementsWithFontFamily.forEach((el) => {
        el.style.fontFamily = 'Poppins';
    });
    // Step 1.6: Normalize line-height to a single value (do NOT touch font-size or font-weight)
    const elementsWithLineHeight = doc.querySelectorAll('[style*="line-height"]');
    elementsWithLineHeight.forEach((el) => {
        el.style.lineHeight = '1.5';
    });
    // Step 2: Handle h2 elements
    const h2Elements = doc.querySelectorAll('h2');
    h2Elements.forEach((h2) => {
        const existingStyle = h2.getAttribute('style') || '';
        const defaultStyles = 'text-transform: uppercase; font-family: "Poppins"; font-size: 20pt; font-weight: bold;';
        let finalStyles = existingStyle;
        if (!existingStyle.includes('text-transform')) {
            finalStyles = `${defaultStyles} ${existingStyle}`.trim();
        }
        if (!finalStyles.includes('font-family')) {
            finalStyles = `${finalStyles}; font-family: "Poppins"`.replace(/^; /, '').replace(/; ;/g, ';');
        }
        if (!finalStyles.includes('font-size')) {
            finalStyles = `${finalStyles}; font-size: 20pt`.replace(/^; /, '').replace(/; ;/g, ';');
        }
        if (!finalStyles.includes('font-weight')) {
            finalStyles = `${finalStyles}; font-weight: bold`.replace(/^; /, '').replace(/; ;/g, ';');
        }
        const hasColorInChildren = h2.querySelector('font[color], span[style*="color"]') !== null;
        if (!hasColorInChildren && !existingStyle.includes('color:')) {
            // Let CSS handle default color
        }
        h2.setAttribute('style', finalStyles);
        if (h2.classList.contains('western')) {
            const currentStyle = h2.getAttribute('style') || '';
            if (!currentStyle.includes('font-family')) {
                const updatedStyle = currentStyle ? `${currentStyle}; font-family: "Poppins"` : 'font-family: "Poppins"';
                h2.setAttribute('style', updatedStyle.replace(/; ;/g, ';'));
            }
        }
    });
    // Step 3: CRITICAL FIX - Unwrap <p> tags inside <li> elements
    // Quill doesn't expect block elements inside list items
    const listItems = doc.querySelectorAll('li');
    listItems.forEach((li) => {
        // Find all direct child <p> tags
        const pTags = Array.from(li.querySelectorAll(':scope > p'));
        pTags.forEach((pTag) => {
            // Preserve any styles from the <p> tag
            const pStyle = pTag.getAttribute('style') || '';
            const pClass = pTag.getAttribute('class') || '';
            // If there are styles or classes, wrap content in a span to preserve them
            if (pStyle || pClass) {
                const span = doc.createElement('span');
                if (pStyle)
                    span.setAttribute('style', pStyle);
                if (pClass)
                    span.setAttribute('class', pClass);
                while (pTag.firstChild) {
                    span.appendChild(pTag.firstChild);
                }
                pTag.parentNode?.replaceChild(span, pTag);
            }
            else {
                // Otherwise, just unwrap - move children directly to li
                const fragment = doc.createDocumentFragment();
                while (pTag.firstChild) {
                    fragment.appendChild(pTag.firstChild);
                }
                pTag.parentNode?.replaceChild(fragment, pTag);
            }
        });
    });
    const listItemsWithP = doc.querySelectorAll('li > p');
    listItemsWithP.forEach((pTag) => {
        const li = pTag.parentElement;
        if (li && li.tagName === 'LI') {
            // Preserve any styles from the <p> tag
            const pStyle = pTag.getAttribute('style') || '';
            const pClass = pTag.getAttribute('class') || '';
            // If there are styles or classes, wrap content in a span to preserve them
            if (pStyle || pClass) {
                const span = doc.createElement('span');
                if (pStyle)
                    span.setAttribute('style', pStyle);
                if (pClass)
                    span.setAttribute('class', pClass);
                while (pTag.firstChild) {
                    span.appendChild(pTag.firstChild);
                }
                pTag.parentNode?.replaceChild(span, pTag);
            }
            else {
                // Otherwise, just unwrap - move children directly to li
                const fragment = doc.createDocumentFragment();
                while (pTag.firstChild) {
                    fragment.appendChild(pTag.firstChild);
                }
                pTag.parentNode?.replaceChild(fragment, pTag);
            }
        }
    });
    // Step 4: Preserve ordered list type attributes (a, b, c vs 1, 2, 3)
    const orderedLists = doc.querySelectorAll('ol');
    orderedLists.forEach((ol) => {
        const typeAttr = ol.getAttribute('type'); // 'a', '1', 'i', 'A', etc.
        // CRITICAL: Preserve the type attribute for CSS to apply correct list-style-type
        if (typeAttr) {
            // Ensure type attribute is preserved on the ol element
            ol.setAttribute('type', typeAttr);
            ol.setAttribute('data-list-type', typeAttr);
            // Also add list-style-type to inline style
            const existingStyle = ol.getAttribute('style') || '';
            let listStyleType = 'decimal'; // default
            if (typeAttr === 'a')
                listStyleType = 'lower-alpha';
            else if (typeAttr === 'A')
                listStyleType = 'upper-alpha';
            else if (typeAttr === 'i')
                listStyleType = 'lower-roman';
            else if (typeAttr === 'I')
                listStyleType = 'upper-roman';
            else if (typeAttr === '1')
                listStyleType = 'decimal';
            if (!existingStyle.includes('list-style-type')) {
                const newStyle = existingStyle
                    ? `${existingStyle}; list-style-type: ${listStyleType}; padding-left: 1.5em; margin: 0.5em 0;`
                    : `list-style-type: ${listStyleType}; padding-left: 1.5em; margin: 0.5em 0;`;
                ol.setAttribute('style', newStyle.replace(/; ;/g, ';'));
            }
            else if (!existingStyle.includes('padding-left')) {
                ol.setAttribute('style', `${existingStyle}; padding-left: 1.5em; margin: 0.5em 0;`.replace(/; ;/g, ';'));
            }
        }
        else {
            // No type specified, use default decimal
            const existingStyle = ol.getAttribute('style') || '';
            if (!existingStyle.includes('padding-left')) {
                const newStyle = existingStyle
                    ? `${existingStyle}; padding-left: 1.5em; margin: 0.5em 0;`
                    : 'padding-left: 1.5em; margin: 0.5em 0;';
                ol.setAttribute('style', newStyle.replace(/; ;/g, ';'));
            }
        }
        // Process each list item - ensure they're properly structured
        const items = ol.querySelectorAll(':scope > li');
        items.forEach((li) => {
            // Ensure list items have proper display
            const liStyle = li.getAttribute('style') || '';
            if (!liStyle.includes('display')) {
                li.setAttribute('style', `${liStyle}; display: list-item`.replace(/^; /, '').replace(/; ;/g, ';'));
            }
        });
    });
    const unorderedLists = doc.querySelectorAll('ul');
    unorderedLists.forEach((ul) => {
        if (!ul.hasAttribute('style')) {
            ul.setAttribute('style', 'padding-left: 1.5em; margin: 0.5em 0;');
        }
        const items = ul.querySelectorAll(':scope > li');
        items.forEach((li) => {
            li.setAttribute('data-list', 'bullet');
        });
    });
    listItems.forEach((li) => {
        const hasStyledContent = li.querySelector('span[style*="font-family"]');
        if (!hasStyledContent) {
            const childNodes = Array.from(li.childNodes);
            childNodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
                    const span = doc.createElement('span');
                    span.style.fontFamily = '"Poppins"';
                    span.textContent = node.textContent;
                    node.parentNode?.replaceChild(span, node);
                }
            });
        }
        const spansWithoutFont = li.querySelectorAll('span:not([style*="font-family"])');
        spansWithoutFont.forEach((span) => {
            const style = span.getAttribute('style') || '';
            span.setAttribute('style', `${style ? style + '; ' : ''}font-family: "Poppins"`);
        });
    });
    return doc.body.innerHTML;
};
/**
 * Extracts body content from HTML string and converts to Quill format
 */
export const extractBodyContent = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const body = doc.querySelector('body');
    let bodyContent = body?.innerHTML || '';
    bodyContent = convertToQuillCompatible(bodyContent);
    return bodyContent;
};
/**
 * Wraps body content in complete HTML document with contract styling
 */
export const createContractHtmlDocument = (bodyContent) => {
    return `<!DOCTYPE html>
<html>
<head>
    <meta http-equiv="content-type" content="text/html; charset=utf-8"/>
    <title>Contract Document</title>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap" rel="stylesheet" />
    <style type="text/css">
        @page { size: 8.27in 11.69in; margin-left: 1in; margin-right: 1in; margin-top: 0.49in; margin-bottom: 0.49in }
        body { font-family: "Poppins"; font-weight: 500; }
        body, body * { font-family: "Poppins" !important; font-style: inherit; }
        p { line-height: 115%; text-align: left; orphans: 2; widows: 2; margin-bottom: 0.1in; direction: ltr; background: transparent }
        p.western { font-family: "Poppins"; font-weight: 500; }
        h2 { text-transform: uppercase; color: #151515; font-family: "Poppins"; font-size: 20pt; font-weight: bold }
        h2.western { font-family: "Poppins"; font-size: 20pt; font-weight: bold }
        h3 { text-transform: uppercase; color: #151515; font-family: "Poppins"; font-size: 16pt; font-weight: bold }
        h3.western { font-family: "Poppins"; font-size: 16pt; font-weight: bold }
        h4 { text-transform: uppercase; color: #151515; font-family: "Poppins"; font-size: 14pt; font-weight: bold }
        h4.western { font-family: "Poppins"; font-size: 14pt; font-weight: bold }
    </style>
</head>
<body lang="nl-NL">
${bodyContent}
</body>
</html>`;
};
